import { WorkCalendar } from "@/engine/calendar";
import { computeSchedule } from "@/engine/cpm";
import { addDays, toDay } from "@/engine/date";
import { analyzeHealth, computeEvm, createBaseline, topDelays, type DelayItem, type EvmSummary, type HealthReport } from "@/engine/health";
import type { Baseline, ExecutionTask, Project, Schedule, UserResource } from "@/engine/types";
import { projectCostSummary } from "@/features/resources/portfolio";
import { displayBaselineName, fmtCurrency, fmtDelta, fmtMM, fmtSpi } from "@/lib/format";
import { formatDate, formatDateRange } from "@/lib/date-format";
import { STATUS_LABEL } from "@/store/execution-task-ops";
import { summarizeRecords } from "@/store/record-ops";

/* ------------------------------------------------------------------------------------------------
 * Report period: a named close of the whole plan at a status date. Unlike a checkpoint (an undo
 * safety net), a period is a reporting boundary — metrics are frozen at close so later edits to
 * the org rate card do not rewrite history.
 * ---------------------------------------------------------------------------------------------- */

export interface ReportMetrics {
  statusDate: string;
  projectStart: string;
  projectFinish: string;
  projectDuration: number;
  /** 0..100, duration-weighted over leaf activities */
  progress: number;
  leafCount: number;
  doneCount: number;
  inProgressCount: number;
  hasBaseline: boolean;
  baselineName?: string;
  /** 0..100 planned value at the status date */
  plannedProgress: number | null;
  spi: number | null;
  finishVarianceDays: number;
  delayCount: number;
  criticalCount: number;
  healthScore: number;
  milestoneCount: number;
  milestoneDone: number;
  headcount: number;
  plannedMM: number;
  costTotal: number;
  budget: number;
  /** 0..n (1 = 100%) */
  burn: number;
  execTotal: number;
  execDone: number;
  execBlocked: number;
  execOverdue: number;
}

export type ReportSource = "manual" | "checkpoint" | "demo";

export interface ReportPeriod {
  id: string;
  name: string;
  note?: string;
  closedAt: string;
  source: ReportSource;
  project: Project;
  metrics: ReportMetrics;
}

export const REPORT_PERIOD_LIMIT = 52;

export interface ReportContext {
  project: Project;
  calendar: WorkCalendar;
  schedule: Schedule;
  evm: EvmSummary;
  delays: DelayItem[];
  health: HealthReport;
  baseline: Baseline | null;
  metrics: ReportMetrics;
}

export function activeBaselineOf(project: Project): Baseline | null {
  return project.baselines.find((b) => b.id === project.activeBaselineId) ?? project.baselines.at(-1) ?? null;
}

function weightedProgress(schedule: Schedule): { progress: number; leaves: number; done: number; inProgress: number } {
  let w = 0;
  let acc = 0;
  let done = 0;
  let inProgress = 0;
  let leaves = 0;
  for (const id of schedule.order) {
    const t = schedule.tasks.get(id)!;
    if (t.isSummary) continue;
    leaves++;
    const weight = Math.max(t.duration, 0.5);
    w += weight;
    acc += weight * t.percentComplete;
    if (t.percentComplete >= 100) done++;
    else if (t.percentComplete > 0) inProgress++;
  }
  return { progress: w ? Math.round(acc / w) : 0, leaves, done, inProgress };
}

/**
 * 진척·SPI·종료 편차·지연 수: 현황과 일정 화면 상태 바가 함께 쓰는 일정 요약.
 * 두 화면의 숫자가 어긋나지 않도록 계산을 한 곳에 둔다(CPM을 다시 돌리지 않고 이미 만든 결과만 받는다).
 */
export interface ScheduleHeadline {
  progress: number;
  hasBaseline: boolean;
  spi: number | null;
  finishVarianceDays: number;
  delayCount: number;
  projectFinish: string;
}

export function scheduleHeadline(parts: { schedule: Schedule; evm: EvmSummary; delays: DelayItem[] }): ScheduleHeadline {
  const { schedule, evm, delays } = parts;
  return {
    progress: weightedProgress(schedule).progress,
    hasBaseline: evm.hasBaseline,
    spi: evm.spi,
    finishVarianceDays: evm.finishVarianceDays,
    delayCount: delays.length,
    projectFinish: schedule.projectFinish,
  };
}

export function computeReportMetrics(
  project: Project,
  parts: { schedule: Schedule; evm: EvmSummary; delays: DelayItem[]; health: HealthReport },
  resources: Record<string, UserResource>,
): ReportMetrics {
  const { schedule, evm, delays, health } = parts;
  const wp = weightedProgress(schedule);
  const head = scheduleHeadline({ schedule, evm, delays });
  const cost = projectCostSummary(project, resources);
  const x = project.executionTasks ?? [];
  const milestones = schedule.order.map((id) => schedule.tasks.get(id)!).filter((t) => t.isMilestone && !t.isSummary);
  return {
    statusDate: project.statusDate,
    projectStart: schedule.projectStart,
    projectFinish: head.projectFinish,
    projectDuration: schedule.projectDuration,
    progress: head.progress,
    leafCount: wp.leaves,
    doneCount: wp.done,
    inProgressCount: wp.inProgress,
    hasBaseline: head.hasBaseline,
    baselineName: evm.baselineName,
    plannedProgress: evm.hasBaseline ? Math.round(evm.pv * 100) : null,
    spi: head.spi,
    finishVarianceDays: head.finishVarianceDays,
    delayCount: head.delayCount,
    criticalCount: schedule.criticalPath.length,
    healthScore: health.score,
    milestoneCount: milestones.length,
    milestoneDone: milestones.filter((m) => m.percentComplete >= 100).length,
    headcount: cost.headcount,
    plannedMM: cost.plannedMM,
    costTotal: cost.total,
    budget: cost.budget,
    burn: cost.burn,
    execTotal: x.length,
    execDone: x.filter((t) => t.status === "done").length,
    execBlocked: x.filter((t) => t.status === "blocked").length,
    execOverdue: x.filter((t) => t.status !== "done" && !!t.due && t.due < project.statusDate).length,
  };
}

export function buildReportContext(project: Project, resources: Record<string, UserResource>, frozen?: ReportMetrics): ReportContext {
  const calendar = new WorkCalendar(project.calendar);
  const schedule = computeSchedule(project, calendar);
  const baseline = activeBaselineOf(project);
  const evm = computeEvm(project, schedule, baseline, calendar);
  const delays = topDelays(project, schedule, baseline, 50, calendar);
  const health = analyzeHealth(project, schedule);
  const live = computeReportMetrics(project, { schedule, evm, delays, health }, resources);
  const metrics = frozen ? { ...live, headcount: frozen.headcount, plannedMM: frozen.plannedMM, costTotal: frozen.costTotal, budget: frozen.budget, burn: frozen.burn } : live;
  return { project, calendar, schedule, evm, delays, health, baseline, metrics };
}

export function makeReportPeriod(
  project: Project,
  resources: Record<string, UserResource>,
  opts: { id: string; name: string; note?: string; closedAt?: string; source?: ReportSource },
): ReportPeriod {
  const snapshot = structuredClone(project);
  const ctx = buildReportContext(snapshot, resources);
  return {
    id: opts.id,
    name: opts.name,
    note: opts.note?.trim() || undefined,
    closedAt: opts.closedAt ?? new Date().toISOString(),
    source: opts.source ?? "manual",
    project: snapshot,
    metrics: ctx.metrics,
  };
}

/** "2026-10-20" → "10월 3주차 주간 보고" */
export function suggestPeriodName(statusDate: string): string {
  const d = Number(statusDate.slice(8, 10));
  return `${formatDate(statusDate, "monthName")} ${Math.ceil(d / 7)}주차 주간 보고`;
}

/* ------------------------------------------------------------------------------------------------
 * Status checks — derived only from numbers the report already shows.
 * ---------------------------------------------------------------------------------------------- */

export type ReportLevel = "good" | "watch" | "risk";

/** 판정 임계값. 판정 계산과 화면에 적는 기준 문구가 같은 값을 쓰도록 한 곳에 둔다. */
export const REPORT_RULES = {
  spiRisk: 0.9,
  spiWatch: 0.97,
  finishRiskDays: 5,
  burnRisk: 1,
  burnWatch: 0.9,
} as const;

/* ------------------------------------------------------------------------------------------------
 * 항목별 점검(체크리스트). 종합 라벨 하나로 뭉치지 않고, 항목마다 상태·근거·기준을 함께 준다.
 * na(판정 불가)는 문제가 아니라 재료가 없다는 뜻이므로 주의·위험으로 세지 않는다.
 * ---------------------------------------------------------------------------------------------- */

export type CheckStatus = "ok" | "watch" | "risk" | "na";
export const CHECK_STATUS_LABEL: Record<CheckStatus, string> = { ok: "정상", watch: "주의", risk: "위험", na: "판정 불가" };

/** 행에서 이동할 수 있는 플랜 탭 */
export type CheckTarget = "schedule" | "tasks" | "records" | "budget";

export interface ReportCheck {
  key: "schedule" | "budget" | "tasks" | "issues" | "milestone";
  label: string;
  status: CheckStatus;
  /** 표의 값 칸. 숫자 위주로 짧게 */
  figure: string;
  /** 값 옆 보조(종료 편차 등). 판정을 가른 숫자와 섞지 않는다 */
  aside?: string;
  /** 모든 행에 적는 기준. 정상 행도 칸을 비우지 않는다 */
  mark: string;
  /** 상태의 근거가 되는 사실 한 문장. 복사·툴팁용 */
  text: string;
  /** 주의·위험일 때 그렇게 판정한 기준(툴팁) */
  rule?: string;
  go?: CheckTarget;
  goLabel?: string;
}

const spi2 = (n: number) => n.toFixed(2);

export function metricChecks(m: ReportMetrics): { schedule: ReportCheck; budget: ReportCheck; tasks: ReportCheck } {
  const R = REPORT_RULES;

  let schedule: ReportCheck;
  const scheduleMark = `SPI ${spi2(R.spiRisk)} 미만`;
  if (!m.hasBaseline) {
    schedule = { key: "schedule", label: "일정", status: "na", figure: "—", mark: "기준선 필요", text: "기준선이 없어 지연을 판정할 수 없어요", go: "schedule", goLabel: "일정에서 저장" };
  } else {
    const risk = (m.spi !== null && m.spi < R.spiRisk) || m.finishVarianceDays > R.finishRiskDays;
    const watch = (m.spi !== null && m.spi < R.spiWatch) || m.finishVarianceDays > 0;
    schedule = {
      key: "schedule",
      label: "일정",
      status: risk ? "risk" : watch ? "watch" : "ok",
      figure: m.spi === null ? `종료 ${m.finishVarianceDays > 0 ? "+" : ""}${m.finishVarianceDays}일` : `SPI ${fmtSpi(m.spi)}`,
      aside: m.spi === null ? undefined : `종료 ${m.finishVarianceDays > 0 ? "+" : ""}${m.finishVarianceDays}일`,
      mark: scheduleMark,
      text: `SPI ${fmtSpi(m.spi)} · 종료일 기준선 대비 ${fmtDelta(m.finishVarianceDays)}`,
      rule: risk ? `SPI ${spi2(R.spiRisk)} 미만 또는 종료일 ${fmtDelta(R.finishRiskDays)} 초과` : watch ? `SPI ${spi2(R.spiWatch)} 미만 또는 종료일 지연` : undefined,
    };
  }

  let budget: ReportCheck;
  const budgetMark = `${Math.round(R.burnRisk * 100)}% 이내`;
  if (m.budget <= 0) {
    budget = { key: "budget", label: "예산", status: "na", figure: "—", mark: "예산 필요", text: "예산이 없어 비용을 판정할 수 없어요", go: "budget", goLabel: "예산 설정" };
  } else {
    const risk = m.burn > R.burnRisk;
    const watch = m.burn > R.burnWatch;
    const pct = Math.round(m.burn * 100);
    budget = {
      key: "budget",
      label: "예산",
      status: risk ? "risk" : watch ? "watch" : "ok",
      figure: `${pct}%`,
      mark: budgetMark,
      text: `계획 비용이 예산의 ${pct}%`,
      rule: risk ? `예산 ${Math.round(R.burnRisk * 100)}% 초과` : watch ? `예산 ${Math.round(R.burnWatch * 100)}% 초과` : undefined,
    };
  }

  let tasks: ReportCheck;
  const taskMark = "막힘·초과 0";
  if (m.execTotal === 0) {
    tasks = { key: "tasks", label: "Task", status: "na", figure: "—", mark: "Task 없음", text: "등록된 Task가 없어요" };
  } else if (m.execBlocked > 0 || m.execOverdue > 0) {
    const figure = [m.execBlocked > 0 ? `막힘 ${m.execBlocked}` : "", m.execOverdue > 0 ? `초과 ${m.execOverdue}` : ""].filter(Boolean).join(" · ");
    const text = [m.execBlocked > 0 ? `막힌 Task ${m.execBlocked}개` : "", m.execOverdue > 0 ? `마감 넘긴 Task ${m.execOverdue}개` : ""].filter(Boolean).join(" · ");
    tasks = { key: "tasks", label: "Task", status: "watch", figure, mark: taskMark, text, rule: "막힘 또는 마감 초과 1개 이상", go: "tasks", goLabel: "Task 열기" };
  } else {
    tasks = { key: "tasks", label: "Task", status: "ok", figure: "0", mark: taskMark, text: "막히거나 마감을 넘긴 Task 없음" };
  }

  return { schedule, budget, tasks };
}

/** 지표만으로 매기는 종합 단계. 추이 표처럼 확정된 지표만 있는 곳에서 쓴다. na는 세지 않는다. */
export function reportLevel(m: ReportMetrics): { level: ReportLevel; reasons: string[] } {
  const { schedule, budget, tasks } = metricChecks(m);
  const list = [schedule, budget, tasks];
  const risk = list.filter((c) => c.status === "risk");
  const watch = list.filter((c) => c.status === "watch");
  if (risk.length) return { level: "risk", reasons: [...risk, ...watch].map((c) => c.text) };
  if (watch.length) return { level: "watch", reasons: watch.map((c) => c.text) };
  return { level: "good", reasons: ["계획 대비 일정·비용 모두 허용 범위 안"] };
}

/** 현황 화면의 점검 항목(고정 순서): 일정 · 예산 · Task · 이슈 · 마일스톤 */
export function reportChecks(ctx: ReportContext, recordDay: string): ReportCheck[] {
  const m = ctx.metrics;
  const { schedule, budget, tasks } = metricChecks(m);

  const rec = summarizeRecords(ctx.project.records ?? [], recordDay);
  let issues: ReportCheck;
  const issueMark = "높음·기한 0";
  if (rec.openIssues === 0) {
    issues = { key: "issues", label: "이슈", status: "ok", figure: "0", mark: issueMark, text: "열린 이슈 없음" };
  } else {
    const attention = rec.highOpenIssues > 0 || rec.overdue > 0;
    const aside = [rec.highOpenIssues > 0 ? `높음 ${rec.highOpenIssues}` : "", rec.overdue > 0 ? `기한 ${rec.overdue}` : ""].filter(Boolean).join(" · ");
    issues = { key: "issues", label: "이슈", status: attention ? "watch" : "ok", figure: `${rec.openIssues}건`, aside: aside || undefined, mark: issueMark, text: [`열린 이슈 ${rec.openIssues}건`, aside].filter(Boolean).join(" · "), rule: attention ? "높음 또는 기한 지남 1건 이상" : undefined, go: "records", goLabel: "이슈 열기" };
  }

  const statusDay = toDay(m.statusDate);
  const nameOf = (id: string) => ctx.project.tasks.find((x) => x.id === id)?.name ?? "—";
  const milestones = ctx.schedule.order.map((id) => ctx.schedule.tasks.get(id)!).filter((t) => t.isMilestone && !t.isSummary);
  let milestone: ReportCheck;
  const late = milestones.filter((t) => t.percentComplete < 100 && toDay(t.start) < statusDay);
  const milestoneMark = "지난 미완료 0";
  if (milestones.length === 0) {
    milestone = { key: "milestone", label: "마일스톤", status: "na", figure: "—", mark: "없음", text: "마일스톤이 없어요" };
  } else if (late.length > 0) {
    milestone = { key: "milestone", label: "마일스톤", status: "watch", figure: `${late.length}개`, mark: milestoneMark, text: `기준일이 지난 미완료 마일스톤 ${late.length}개`, rule: "기준일이 지난 미완료 마일스톤 1개 이상", go: "schedule", goLabel: "일정 열기" };
  } else {
    const next = milestones.filter((t) => t.percentComplete < 100 && toDay(t.start) - statusDay <= 14).slice(0, 3);
    const names = next.map((t) => `${nameOf(t.id)}(${formatDate(t.start, "short")})`);
    const done = milestones.filter((t) => t.percentComplete >= 100).length;
    milestone = { key: "milestone", label: "마일스톤", status: "ok", figure: names[0] ?? `${done}/${milestones.length}`, aside: names.length > 1 ? `외 ${names.length - 1}` : names.length === 0 ? "2주 안" : undefined, mark: milestoneMark, text: names.length ? `다음 2주 · ${names.join(", ")}` : "다음 2주 안에 예정된 마일스톤 없음" };
  }

  return [schedule, budget, tasks, issues, milestone];
}

/** 머리글용 집계: 확인이 필요한 항목(주의+위험)과 판정 불가 항목 수 */
export function countChecks(checks: readonly ReportCheck[]): { attention: number; unknown: number } {
  return {
    attention: checks.filter((c) => c.status === "watch" || c.status === "risk").length,
    unknown: checks.filter((c) => c.status === "na").length,
  };
}

/* ------------------------------------------------------------------------------------------------
 * Change set between two points (period ↔ period, checkpoint ↔ now, …)
 * ---------------------------------------------------------------------------------------------- */

export type ChangeCategory = "scope" | "schedule" | "progress" | "team" | "budget" | "tasks";
export const CHANGE_CATEGORIES: ChangeCategory[] = ["scope", "schedule", "progress", "team", "budget", "tasks"];
export const CHANGE_CATEGORY_LABEL: Record<ChangeCategory, string> = {
  scope: "WBS 작업",
  schedule: "일정",
  progress: "진척률",
  team: "인력",
  budget: "예산",
  tasks: "실행 Task",
};

/** 각 구분이 세는 것. 현황 차트의 도움말과 변경 내역이 같은 말을 쓴다. */
export const CHANGE_CATEGORY_HINT: Record<ChangeCategory, string> = {
  scope: "추가·삭제, 이름, 상위, 연결",
  schedule: "기간, 종료일, 마감일",
  progress: "완료 비율이 바뀐 작업",
  team: "인원, 역할, 공수",
  budget: "예산, 비용, 솔루션",
  tasks: "추가·삭제, 상태, 마감",
};

export type ChangeKind = "added" | "removed" | "changed";
export const CHANGE_KIND_LABEL: Record<ChangeKind, string> = { added: "추가", removed: "삭제", changed: "변경" };

export type ChangeTone = "danger" | "warning" | "success";

/** money values stay numeric so the page can mask them per view role */
export type ChangeValue = string | { money: number };

export interface ChangeItem {
  id: string;
  category: ChangeCategory;
  kind: ChangeKind;
  subject: string;
  /** WBS code or other short identifier */
  code?: string;
  field: string;
  before?: ChangeValue;
  after?: ChangeValue;
  delta?: ChangeValue;
  tone?: ChangeTone;
  /** where to jump from the row */
  link?: { tab: "schedule" | "team" | "budget" | "tasks"; taskId?: string; executionId?: string };
}

const pct = (n: number) => `${Math.round(n)}%`;
const pp = (n: number) => (n === 0 ? "±0%p" : `${n > 0 ? "+" : ""}${Math.round(n)}%p`);
const signedMM = (n: number) => `${n > 0 ? "+" : ""}${Number(n.toFixed(2))} M/M`;
const money = (n: number): ChangeValue => ({ money: n });

function resourceName(resources: Record<string, UserResource>, id: string): string {
  return resources[id]?.displayName ?? "알 수 없는 인력";
}

function execStatusLabel(t: ExecutionTask): string {
  return t.status ? STATUS_LABEL[t.status] : "상태 미정";
}

export function diffReportContexts(a: ReportContext, b: ReportContext, resources: Record<string, UserResource>): ChangeItem[] {
  const out: ChangeItem[] = [];
  let seq = 0;
  const push = (item: Omit<ChangeItem, "id">) => out.push({ id: `ch${seq++}`, ...item });
  const pa = a.project;
  const pb = b.project;
  const cal = b.calendar;
  const code = (id: string) => b.schedule.tasks.get(id)?.wbs ?? a.schedule.tasks.get(id)?.wbs;

  /* ---- project-level schedule headline ---- */
  if (a.schedule.projectFinish !== b.schedule.projectFinish) {
    const d = cal.ordinalBeforeISO(b.schedule.projectFinish) - cal.ordinalBeforeISO(a.schedule.projectFinish);
    push({ category: "schedule", kind: "changed", subject: "플랜 종료일", field: "종료", before: formatDate(a.schedule.projectFinish, "numeric"), after: formatDate(b.schedule.projectFinish, "numeric"), delta: fmtDelta(d), tone: d > 0 ? "danger" : "success", link: { tab: "schedule" } });
  }
  if (pa.statusDate !== pb.statusDate) {
    push({ category: "schedule", kind: "changed", subject: "진척 기준일", field: "기준일", before: formatDate(pa.statusDate, "numeric"), after: formatDate(pb.statusDate, "numeric"), delta: `${toDay(pb.statusDate) - toDay(pa.statusDate)}일`, link: { tab: "schedule" } });
  }
  const blA = new Set(pa.baselines.map((x) => x.id));
  for (const bl of pb.baselines) if (!blA.has(bl.id)) push({ category: "schedule", kind: "added", subject: displayBaselineName(bl.name), field: "기준선", after: `종료 ${formatDate(bl.projectFinish, "numeric")}`, link: { tab: "schedule" } });
  const blB = new Set(pb.baselines.map((x) => x.id));
  for (const bl of pa.baselines) if (!blB.has(bl.id)) push({ category: "schedule", kind: "removed", subject: displayBaselineName(bl.name), field: "기준선", before: `종료 ${formatDate(bl.projectFinish, "numeric")}`, tone: "warning" });

  /* ---- WBS scope, schedule and progress ---- */
  const ta = new Map(pa.tasks.map((t) => [t.id, t]));
  const tb = new Map(pb.tasks.map((t) => [t.id, t]));
  const nameOf = (id: string | null, m: Map<string, (typeof pa.tasks)[number]>) => (id ? (m.get(id)?.name ?? "—") : "최상위");

  for (const t of pb.tasks) {
    const prev = ta.get(t.id);
    const sb = b.schedule.tasks.get(t.id);
    if (!prev) {
      push({
        category: "scope",
        kind: "added",
        subject: t.name || "이름 없는 작업",
        code: code(t.id),
        field: sb?.isSummary ? "단계" : sb?.isMilestone ? "마일스톤" : "작업",
        after: sb ? (sb.isMilestone ? formatDate(sb.start, "short") : `${sb.duration}d · ${formatDateRange(sb.start, sb.finish, "short")}`) : undefined,
        link: { tab: "schedule", taskId: t.id },
      });
      continue;
    }
    const sa = a.schedule.tasks.get(t.id);
    if (prev.name !== t.name) push({ category: "scope", kind: "changed", subject: t.name || "이름 없는 작업", code: code(t.id), field: "이름", before: prev.name, after: t.name, link: { tab: "schedule", taskId: t.id } });
    if (prev.parentId !== t.parentId) push({ category: "scope", kind: "changed", subject: t.name, code: code(t.id), field: "상위 단계", before: nameOf(prev.parentId, ta), after: nameOf(t.parentId, tb), link: { tab: "schedule", taskId: t.id } });
    if (!sa || !sb) continue;
    if (sb.isSummary) continue;
    if (!sa.isSummary && sa.duration !== sb.duration) {
      const d = sb.duration - sa.duration;
      push({ category: "schedule", kind: "changed", subject: t.name, code: code(t.id), field: "기간", before: `${sa.duration}d`, after: `${sb.duration}d`, delta: fmtDelta(d), tone: d > 0 ? "warning" : undefined, link: { tab: "schedule", taskId: t.id } });
    }
    if (sa.finish !== sb.finish) {
      const d = cal.ordinalBeforeISO(sb.finish) - cal.ordinalBeforeISO(sa.finish);
      if (d !== 0) push({ category: "schedule", kind: "changed", subject: t.name, code: code(t.id), field: "종료", before: formatDate(sa.finish, "short"), after: formatDate(sb.finish, "short"), delta: fmtDelta(d), tone: d > 0 && sb.critical ? "danger" : d > 0 ? "warning" : "success", link: { tab: "schedule", taskId: t.id } });
    }
    if ((prev.deadline ?? "") !== (t.deadline ?? "")) push({ category: "schedule", kind: "changed", subject: t.name, code: code(t.id), field: "마감일", before: prev.deadline ? formatDate(prev.deadline, "numeric") : "없음", after: t.deadline ? formatDate(t.deadline, "numeric") : "없음", link: { tab: "schedule", taskId: t.id } });
    if (sa.percentComplete !== sb.percentComplete) {
      const d = sb.percentComplete - sa.percentComplete;
      push({ category: "progress", kind: "changed", subject: t.name, code: code(t.id), field: sb.percentComplete >= 100 ? "완료" : "진척", before: pct(sa.percentComplete), after: pct(sb.percentComplete), delta: pp(d), tone: sb.percentComplete >= 100 ? "success" : d < 0 ? "warning" : undefined, link: { tab: "schedule", taskId: t.id } });
    }
  }
  for (const t of pa.tasks) {
    if (tb.has(t.id)) continue;
    const sa = a.schedule.tasks.get(t.id);
    push({ category: "scope", kind: "removed", subject: t.name || "이름 없는 작업", code: sa?.wbs, field: sa?.isSummary ? "단계" : sa?.isMilestone ? "마일스톤" : "작업", before: sa ? `${sa.duration}d · ${formatDateRange(sa.start, sa.finish, "short")}` : undefined, tone: "warning" });
  }

  const depKey = (d: { predecessorId: string; successorId: string }) => `${d.predecessorId}>${d.successorId}`;
  const da = new Map(pa.dependencies.map((d) => [depKey(d), d]));
  const db = new Map(pb.dependencies.map((d) => [depKey(d), d]));
  const depLabel = (d: { predecessorId: string; successorId: string }, m: Map<string, (typeof pa.tasks)[number]>) => `${m.get(d.predecessorId)?.name ?? "—"} → ${m.get(d.successorId)?.name ?? "—"}`;
  const depSpec = (d: { type: string; lag: number }) => (d.lag ? `${d.type} ${d.lag > 0 ? "+" : ""}${d.lag}d` : d.type);
  for (const [k, d] of db) {
    const prev = da.get(k);
    if (!prev) push({ category: "scope", kind: "added", subject: depLabel(d, tb), field: "연결", after: depSpec(d), link: { tab: "schedule", taskId: d.successorId } });
    else if (prev.type !== d.type || prev.lag !== d.lag) push({ category: "scope", kind: "changed", subject: depLabel(d, tb), field: "연결", before: depSpec(prev), after: depSpec(d), link: { tab: "schedule", taskId: d.successorId } });
  }
  for (const [k, d] of da) if (!db.has(k)) push({ category: "scope", kind: "removed", subject: depLabel(d, ta), field: "연결", before: depSpec(d) });

  /* ---- team ---- */
  const aa = new Map(pa.resourceAssignments.map((x) => [x.id, x]));
  const ab = new Map(pb.resourceAssignments.map((x) => [x.id, x]));
  for (const x of pb.resourceAssignments) {
    const prev = aa.get(x.id);
    const who = resourceName(resources, x.resourceId);
    if (!prev) {
      push({ category: "team", kind: "added", subject: who, code: x.projectRole, field: "투입", after: `${fmtMM(x.plannedMM)} · ${Math.round(x.allocationRatio * 100)}%`, delta: signedMM(x.plannedMM), link: { tab: "team" } });
      continue;
    }
    if (prev.plannedMM !== x.plannedMM) push({ category: "team", kind: "changed", subject: who, code: x.projectRole, field: "계획 M/M", before: fmtMM(prev.plannedMM), after: fmtMM(x.plannedMM), delta: signedMM(x.plannedMM - prev.plannedMM), link: { tab: "team" } });
    if (prev.allocationRatio !== x.allocationRatio) push({ category: "team", kind: "changed", subject: who, code: x.projectRole, field: "투입률", before: pct(prev.allocationRatio * 100), after: pct(x.allocationRatio * 100), delta: pp((x.allocationRatio - prev.allocationRatio) * 100), link: { tab: "team" } });
    if (prev.startDate !== x.startDate || prev.endDate !== x.endDate) push({ category: "team", kind: "changed", subject: who, code: x.projectRole, field: "투입 기간", before: formatDateRange(prev.startDate, prev.endDate, "numeric"), after: formatDateRange(x.startDate, x.endDate, "numeric"), link: { tab: "team" } });
    if (prev.projectRole !== x.projectRole) push({ category: "team", kind: "changed", subject: who, field: "역할", before: prev.projectRole, after: x.projectRole, link: { tab: "team" } });
  }
  for (const x of pa.resourceAssignments) if (!ab.has(x.id)) push({ category: "team", kind: "removed", subject: resourceName(resources, x.resourceId), code: x.projectRole, field: "투입", before: fmtMM(x.plannedMM), delta: signedMM(-x.plannedMM), tone: "warning" });

  /* ---- budget ---- */
  const bgA = pa.budget;
  const bgB = pb.budget;
  const budgetFields: Array<[keyof typeof bgA, string]> = [
    ["totalBudget", "총 예산"],
    ["targetLaborCost", "목표 인건비"],
    ["targetSolutionCost", "목표 솔루션비"],
    ["targetExpenseCost", "직접경비"],
    ["contingencyCost", "예비비"],
  ];
  for (const [k, label] of budgetFields) {
    const x = bgA[k] as number;
    const y = bgB[k] as number;
    if (x !== y) push({ category: "budget", kind: "changed", subject: label, field: "예산", before: money(x), after: money(y), delta: money(y - x), link: { tab: "budget" } });
  }
  if (a.metrics.costTotal !== b.metrics.costTotal) {
    const d = b.metrics.costTotal - a.metrics.costTotal;
    push({ category: "budget", kind: "changed", subject: "계획 비용 합계", field: "비용", before: money(a.metrics.costTotal), after: money(b.metrics.costTotal), delta: money(d), tone: d > 0 && b.metrics.burn > 1 ? "danger" : d > 0 ? "warning" : "success", link: { tab: "budget" } });
  }
  const sa = new Map(pa.solutions.map((x) => [x.id, x]));
  const sb = new Map(pb.solutions.map((x) => [x.id, x]));
  for (const s of pb.solutions) {
    const prev = sa.get(s.id);
    if (!prev) push({ category: "budget", kind: "added", subject: s.name, code: s.vendor, field: "솔루션", after: money(s.totalCost), delta: money(s.totalCost), link: { tab: "budget" } });
    else if (prev.totalCost !== s.totalCost) push({ category: "budget", kind: "changed", subject: s.name, code: s.vendor, field: "솔루션 비용", before: money(prev.totalCost), after: money(s.totalCost), delta: money(s.totalCost - prev.totalCost), link: { tab: "budget" } });
    else if (prev.status !== s.status) push({ category: "budget", kind: "changed", subject: s.name, code: s.vendor, field: "계약 상태", before: prev.status, after: s.status, link: { tab: "budget" } });
  }
  for (const s of pa.solutions) if (!sb.has(s.id)) push({ category: "budget", kind: "removed", subject: s.name, code: s.vendor, field: "솔루션", before: money(s.totalCost), delta: money(-s.totalCost) });

  /* ---- execution tasks ---- */
  const xa = new Map(pa.executionTasks.map((x) => [x.id, x]));
  const xb = new Map(pb.executionTasks.map((x) => [x.id, x]));
  for (const x of pb.executionTasks) {
    const prev = xa.get(x.id);
    if (!prev) {
      push({ category: "tasks", kind: "added", subject: x.title || "제목 없는 Task", field: "Task", after: execStatusLabel(x), link: { tab: "tasks", executionId: x.id } });
      continue;
    }
    if (prev.status !== x.status) push({ category: "tasks", kind: "changed", subject: x.title || "제목 없는 Task", field: "상태", before: execStatusLabel(prev), after: execStatusLabel(x), tone: x.status === "done" ? "success" : x.status === "blocked" ? "danger" : undefined, link: { tab: "tasks", executionId: x.id } });
    if ((prev.due ?? "") !== (x.due ?? "")) {
      const d = prev.due && x.due ? toDay(x.due) - toDay(prev.due) : 0;
      push({ category: "tasks", kind: "changed", subject: x.title || "제목 없는 Task", field: "마감", before: prev.due ? formatDate(prev.due, "short") : "없음", after: x.due ? formatDate(x.due, "short") : "없음", delta: d ? `${d > 0 ? "+" : ""}${d}일` : undefined, tone: d > 0 ? "warning" : undefined, link: { tab: "tasks", executionId: x.id } });
    }
  }
  for (const x of pa.executionTasks) if (!xb.has(x.id)) push({ category: "tasks", kind: "removed", subject: x.title || "제목 없는 Task", field: "Task", before: execStatusLabel(x) });

  return out;
}

export function countByCategory(items: ChangeItem[]): Record<ChangeCategory, number> {
  const c = { scope: 0, schedule: 0, progress: 0, team: 0, budget: 0, tasks: 0 } as Record<ChangeCategory, number>;
  for (const i of items) c[i.category]++;
  return c;
}

/* ------------------------------------------------------------------------------------------------
 * Narrative: the weekly report body is generated, not written.
 * ---------------------------------------------------------------------------------------------- */

export interface ReportNarrative {
  headline: string;
  lines: string[];
}

export function buildNarrative(cur: ReportContext, prev: ReportContext | null, items: ChangeItem[], masked: boolean): ReportNarrative {
  const m = cur.metrics;
  const lines: string[] = [];
  const progressDelta = prev ? m.progress - prev.metrics.progress : null;
  const headline = `전체 진척 ${m.progress}%${progressDelta !== null ? ` (${pp(progressDelta)})` : ""}${m.plannedProgress !== null ? `, 계획 ${m.plannedProgress}%` : ""}${m.hasBaseline ? ` · 종료일 기준선 대비 ${fmtDelta(m.finishVarianceDays)}` : ""}`;

  const completed = items.filter((i) => i.category === "progress" && i.field === "완료");
  const topDelay = cur.delays[0];
  const doneNow = prev ? completed.length : m.doneCount;
  lines.push(
    (doneNow ? `${prev ? "이번 기간" : "현재까지"} 작업 ${doneNow}개를 완료했고, ${m.inProgressCount}개가 진행 중입니다.` : `${prev ? "이번 기간에" : "아직"} 완료한 작업은 없고, ${m.inProgressCount}개가 진행 중입니다.`) +
      (m.delayCount ? ` 기준선보다 늦어진 작업은 ${m.delayCount}개이며, 가장 큰 지연은 「${topDelay?.name}」 ${fmtDelta(topDelay?.varianceDays ?? 0)}입니다.` : m.hasBaseline ? " 기준선보다 늦어진 작업은 없습니다." : ""),
  );
  if (m.spi !== null) {
    lines.push(m.spi < 0.9 ? `SPI ${fmtSpi(m.spi)} — 계획보다 많이 늦습니다. 여유 없는 작업 ${m.criticalCount}개의 만회 계획이 필요합니다.` : m.spi < 1 ? `SPI ${fmtSpi(m.spi)} — 계획보다 조금 늦습니다.` : `SPI ${fmtSpi(m.spi)} — 계획대로 진행 중입니다.`);
  }
  if (prev) {
    const count = (cat: ChangeCategory, kind?: ChangeKind) => items.filter((i) => i.category === cat && (!kind || i.kind === kind)).length;
    const scopeAdd = items.filter((i) => i.category === "scope" && i.kind === "added" && i.field !== "연결").length;
    const scopeDel = items.filter((i) => i.category === "scope" && i.kind === "removed" && i.field !== "연결").length;
    const parts: string[] = [];
    if (scopeAdd || scopeDel) parts.push(`범위 작업 +${scopeAdd}/−${scopeDel}`);
    const mmDelta = m.plannedMM - prev.metrics.plannedMM;
    if (count("team")) parts.push(`인력 ${m.headcount - prev.metrics.headcount >= 0 ? "+" : ""}${m.headcount - prev.metrics.headcount}명 · ${signedMM(mmDelta)}`);
    const costDelta = m.costTotal - prev.metrics.costTotal;
    if (costDelta !== 0) parts.push(`계획 비용 ${masked ? "변경" : `${costDelta > 0 ? "+" : "−"}${fmtCurrency(Math.abs(costDelta))}`}`);
    const finishDelta = cur.calendar.ordinalBeforeISO(m.projectFinish) - cur.calendar.ordinalBeforeISO(prev.metrics.projectFinish);
    if (finishDelta !== 0) parts.push(`종료일 ${fmtDelta(finishDelta)}`);
    lines.push(parts.length ? `직전 마감 대비 계획 변경: ${parts.join(", ")}.` : "직전 마감 대비 범위·인력·예산 변경은 없습니다.");
  }
  const statusDay = toDay(m.statusDate);
  const next = cur.schedule.order
    .map((id) => cur.schedule.tasks.get(id)!)
    .filter((t) => t.isMilestone && t.percentComplete < 100 && toDay(t.start) >= statusDay && toDay(t.start) - statusDay <= 14)
    .slice(0, 3)
    .map((t) => `${cur.project.tasks.find((x) => x.id === t.id)?.name}(${formatDate(t.start, "short")})`);
  lines.push(next.length ? `다음 2주 마일스톤: ${next.join(", ")}.` : "다음 2주 안에 예정된 마일스톤은 없습니다.");
  if (m.execBlocked || m.execOverdue) lines.push(`실행 Task 중 막힘 ${m.execBlocked}개, 마감 넘김 ${m.execOverdue}개를 확인해 주세요.`);
  return { headline, lines };
}

/** 요약 복사용 텍스트: 화면의 점검 항목과 같은 판정을 먼저, 서술은 뒤에 붙인다. */
export function narrativeToText(projectName: string, periodLabel: string, checks: readonly ReportCheck[], n: ReportNarrative): string {
  const { attention, unknown } = countChecks(checks);
  const flag = `${attention ? `확인 필요 ${attention}` : "확인 필요 없음"}${unknown ? ` · 판정 불가 ${unknown}` : ""}`;
  return [
    `[${projectName}] ${periodLabel} — ${flag}`,
    n.headline,
    "",
    "점검",
    ...checks.map((c) => `- ${c.label} · ${CHECK_STATUS_LABEL[c.status]}: ${c.text}${c.rule ? ` (기준 ${c.rule})` : ""}`),
    "",
    "내용",
    ...n.lines.map((l) => `- ${l}`),
  ].join("\n");
}

/* ------------------------------------------------------------------------------------------------
 * Demo history: roll the current plan back week by week so the report page has something real
 * to compare. Each step undoes one believable change (scope added, duration stretched, people
 * joined), so the diff between periods reads like a real project.
 * ---------------------------------------------------------------------------------------------- */

const round5 = (n: number) => Math.max(0, Math.min(100, Math.round(n / 5) * 5));

function removeTask(p: Project, id: string): Project {
  return { ...p, tasks: p.tasks.filter((t) => t.id !== id), dependencies: p.dependencies.filter((d) => d.predecessorId !== id && d.successorId !== id), executionTasks: p.executionTasks.map((x) => ({ ...x, linkedTaskIds: x.linkedTaskIds.filter((l) => l !== id) })) };
}

/** Undo one week of earned work, most recently finishing work first, at the plan's average weekly pace. */
function rollbackProgress(p: Project, statusDate: string, weeklyDays: number): Project {
  const schedule = computeSchedule(p);
  const leaves = schedule.order
    .map((id) => schedule.tasks.get(id)!)
    .filter((s) => !s.isSummary && s.percentComplete > 0)
    .sort((a, b) => toDay(b.finish) - toDay(a.finish));
  const next = new Map<string, number>();
  let budget = weeklyDays;
  for (const s of leaves) {
    if (budget <= 0) break;
    if (s.duration === 0) {
      next.set(s.id, 0);
      continue;
    }
    const earned = (s.percentComplete / 100) * s.duration;
    const take = Math.min(budget, earned);
    budget -= take;
    next.set(s.id, round5(((earned - take) / s.duration) * 100));
  }
  return {
    ...p,
    statusDate,
    tasks: p.tasks.map((t) => (next.has(t.id) ? { ...t, percentComplete: next.get(t.id)! } : t)),
    executionTasks: p.executionTasks.map((x, i) => {
      if (x.status === "done" && i % 2 === 0) {
        const { completedAt: _c, ...rest } = x;
        return { ...rest, status: "inProgress" as const };
      }
      if (x.status === "inProgress" && i % 3 === 0) return { ...x, status: "notStarted" as const };
      return x;
    }),
  };
}

/** what happened in the week leading up to each demo close (index 0 = most recent) */
const DEMO_NOTES = ["인력 1명 합류, 솔루션 1건 계약, 핵심 경로 작업 기간 조정", "범위 1건 추가 협의, 예비비 증액 승인", "착수 후 첫 주간 보고"];

export function buildDemoHistory(current: Project, resources: Record<string, UserResource>, weeks = 3): { project: Project; periods: ReportPeriod[] } {
  const cur = structuredClone(current);
  const schedule = computeSchedule(cur);
  const leaf = (id: string) => {
    const s = schedule.tasks.get(id);
    return !!s && !s.isSummary && !s.isMilestone;
  };
  const byPct = new Map(cur.tasks.map((t) => [t.id, t.percentComplete]));
  const critical = schedule.criticalPath.filter((id) => leaf(id) && (byPct.get(id) ?? 0) < 100 && (schedule.tasks.get(id)?.duration ?? 0) >= 3);
  const late = [...schedule.order].reverse().filter((id) => leaf(id) && (byPct.get(id) ?? 0) === 0 && !critical.includes(id));
  const added = late.slice(0, 2);
  let earnedDays = 0;
  for (const id of schedule.order) {
    const s = schedule.tasks.get(id)!;
    if (!s.isSummary) earnedDays += (s.percentComplete / 100) * s.duration;
  }
  const weeksElapsed = Math.max(1, (toDay(cur.statusDate) - toDay(cur.start)) / 7);
  const weeklyDays = Math.max(1, earnedDays / weeksElapsed);

  const versions: Project[] = [cur];
  for (let w = 1; w <= weeks; w++) {
    let p = structuredClone(versions[w - 1]!);
    const stretch = critical[w - 1];
    if (stretch) p = { ...p, tasks: p.tasks.map((t) => (t.id === stretch ? { ...t, duration: Math.max(1, t.duration - (w === 1 ? 3 : 2)) } : t)) };
    if (w === 1) {
      if (added[0]) p = removeTask(p, added[0]);
      const newest = [...p.executionTasks].sort((x, y) => y.order - x.order).slice(0, 2).map((x) => x.id);
      p = { ...p, executionTasks: p.executionTasks.filter((x) => !newest.includes(x.id)) };
    }
    if (w === 2) {
      const lastAsg = p.resourceAssignments.at(-1);
      p = {
        ...p,
        resourceAssignments: p.resourceAssignments.filter((x) => x.id !== lastAsg?.id).map((x, i) => (i === 0 ? { ...x, plannedMM: Math.max(0.5, Math.round((x.plannedMM - 0.5) * 10) / 10) } : x)),
        solutions: p.solutions.slice(0, Math.max(0, p.solutions.length - 1)),
      };
    }
    if (w === 3) {
      if (added[1]) p = removeTask(p, added[1]);
      p = { ...p, budget: { ...p.budget, contingencyCost: Math.round(p.budget.contingencyCost * 0.8) } };
    }
    p = rollbackProgress(p, addDays(cur.statusDate, -7 * w), weeklyDays);
    versions.push(p);
  }

  if (!cur.baselines.length) {
    const oldest = versions[weeks]!;
    const bl = createBaseline(oldest, computeSchedule(oldest), "BL-1 착수 승인 계획", "착수 보고 때 승인한 계획");
    bl.createdAt = `${addDays(oldest.statusDate, -1)}T09:00:00.000Z`;
    for (const v of versions) {
      v.baselines = [bl];
      v.activeBaselineId = bl.id;
    }
  }

  const periods: ReportPeriod[] = [];
  for (let w = 1; w <= weeks; w++) {
    const v = versions[w]!;
    periods.push(
      makeReportPeriod(v, resources, {
        id: `rp_demo_${w}_${Date.now().toString(36)}`,
        name: suggestPeriodName(v.statusDate),
        note: DEMO_NOTES[w - 1],
        closedAt: `${v.statusDate}T09:00:00.000Z`,
        source: "demo",
      }),
    );
  }
  return { project: versions[0]!, periods };
}
